import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExperienceAudio } from './utils/state/experienceSlice';
import { updateScreen } from './utils/state/screensSlice';
import AssetPicker from '../v2/components/AssetPicker';

/** Nonvisual page audio, stored with the experience and editable without JSON. */
export default function ExperienceAudioEditor({
    screen,
    experienceId,
}: {
    screen: ScreenDefinition;
    experienceId: number;
}) {
    const dispatch = useDispatch();
    const experience = useSelector(
        (s: any) => s.experiences.present.entities[experienceId],
    );
    const screens = useSelector((s: any) => s.screens.present.entities);
    const [error, setError] = useState('');
    const [chooseLibrary, setChooseLibrary] = useState(false);
    const audio: ExperienceAudioSettings = experience?.audio ?? {
        assets: {},
        masterVolume: 0.65,
        maximumVolume: 0.85,
        showVolumeControls: true,
    };
    const playback: ScreenAudioPlayback = screen.audio ?? { cues: [] };
    const saveAudio = (next: ExperienceAudioSettings) =>
        dispatch(setExperienceAudio({ experienceId, audio: next }));
    const savePlayback = (patch: Partial<ScreenAudioPlayback>) =>
        dispatch(
            updateScreen({
                id: screen.id,
                screen: { ...screen, audio: { ...playback, ...patch } },
            }),
        );
    const field = 'w-full rounded border p-2 text-sm';
    const completion = playback.onComplete?.[0];
    const addFile = async (file?: File) => {
        if (!file) return;
        if (
            !/\.(wav|mp3|m4a)$/i.test(file.name) ||
            file.size > 10 * 1024 * 1024
        ) {
            setError('Choose a WAV, MP3, or M4A file up to 10 MB.');
            return;
        }
        try {
            const source = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () =>
                    resolve(
                        String(reader.result).replace(
                            /^data:[^;]+;/,
                            'data:audio/' +
                                (/\.wav$/i.test(file.name)
                                    ? 'wav'
                                    : /\.mp3$/i.test(file.name)
                                      ? 'mpeg'
                                      : 'mp4') +
                                ';',
                        ),
                    );
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const base = file.name
                .replace(/\.[^.]+$/, '')
                .replace(/[^a-zA-Z0-9_-]/g, '-');
            let id = base;
            let suffix = 2;
            while (audio.assets[id]) id = `${base}-${suffix++}`;
            saveAudio({
                ...audio,
                assets: { ...audio.assets, [id]: { source } },
            });
            setError('');
        } catch {
            setError('Could not read the audio file.');
        }
    };
    return (
        <details className="rounded border bg-white p-3">
            <summary className="cursor-pointer font-medium">
                Audio & telephone
            </summary>
            <div className="mt-3 space-y-3 text-sm">
                <p>
                    New files upload to the asset library when you save. The
                    booth downloads and caches the audio before use.
                </p>
                <label className="block">
                    Add audio file
                    <input
                        className={field}
                        type="file"
                        accept=".wav,.mp3,.m4a,audio/*"
                        onChange={(e) => void addFile(e.target.files?.[0])}
                    />
                </label>
                <button
                    type="button"
                    className="rounded border px-3 py-2"
                    onClick={() => setChooseLibrary(true)}
                >
                    Choose audio from library
                </button>
                {chooseLibrary && (
                    <AssetPicker
                        mediaKind="audio"
                        onClose={() => setChooseLibrary(false)}
                        onSelect={(source) => {
                            let id = 'library-audio';
                            let suffix = 2;
                            while (audio.assets[id])
                                id = `library-audio-${suffix++}`;
                            saveAudio({
                                ...audio,
                                assets: { ...audio.assets, [id]: { source } },
                            });
                            setChooseLibrary(false);
                        }}
                    />
                )}
                {error && (
                    <p role="alert" className="text-red-700">
                        {error}
                    </p>
                )}
                <label className="block">
                    Default playback volume:{' '}
                    {Math.round((audio.masterVolume ?? 0.65) * 100)}%
                    <input
                        className="w-full"
                        type="range"
                        min="0"
                        max={audio.maximumVolume ?? 0.85}
                        step="0.05"
                        value={audio.masterVolume ?? 0.65}
                        onChange={(e) =>
                            saveAudio({
                                ...audio,
                                masterVolume: Number(e.target.value),
                            })
                        }
                    />
                </label>
                <label className="block">
                    Maximum guest volume
                    <input
                        className={field}
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={audio.maximumVolume ?? 0.85}
                        onChange={(e) => {
                            const maximum = Math.min(
                                1,
                                Math.max(0, Number(e.target.value)),
                            );
                            saveAudio({
                                ...audio,
                                maximumVolume: maximum,
                                masterVolume: Math.min(
                                    audio.masterVolume ?? 0.65,
                                    maximum,
                                ),
                            });
                        }}
                    />
                </label>
                <label className="flex gap-2">
                    <input
                        type="checkbox"
                        checked={audio.showVolumeControls !== false}
                        onChange={(e) =>
                            saveAudio({
                                ...audio,
                                showVolumeControls: e.target.checked,
                            })
                        }
                    />
                    Show guest volume buttons
                </label>
                <p className="font-medium">This page's audio sequence</p>
                <select
                    className={field}
                    value=""
                    onChange={(e) => {
                        if (e.target.value)
                            savePlayback({
                                cues: [...playback.cues, e.target.value],
                            });
                    }}
                >
                    <option value="">Add a sound…</option>
                    {Object.keys(audio.assets).map((id) => (
                        <option key={id} value={id}>
                            {id}
                        </option>
                    ))}
                </select>
                {playback.cues.map((id, index) => (
                    <div
                        key={`${index}-${id}`}
                        className="space-y-1 rounded border p-2"
                    >
                        <div className="flex justify-between">
                            <span>
                                {index + 1}. {id}
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    savePlayback({
                                        cues: playback.cues.filter(
                                            (_, i) => i !== index,
                                        ),
                                    })
                                }
                            >
                                Remove
                            </button>
                        </div>
                        <audio
                            className="w-full"
                            controls
                            preload="none"
                            src={audio.assets[id]?.source}
                        />
                        <textarea
                            aria-label={`Transcript for ${id}`}
                            className={field}
                            placeholder="Transcript / caption"
                            value={audio.assets[id]?.transcript ?? ''}
                            onChange={(e) =>
                                saveAudio({
                                    ...audio,
                                    assets: {
                                        ...audio.assets,
                                        [id]: {
                                            ...audio.assets[id],
                                            transcript: e.target.value,
                                        },
                                    },
                                })
                            }
                        />
                    </div>
                ))}
                {!!screen.audio && (
                    <>
                        <label className="flex gap-2">
                            <input
                                type="checkbox"
                                checked={playback.loop === true}
                                onChange={(e) =>
                                    savePlayback({
                                        loop: e.target.checked,
                                        onComplete: e.target.checked
                                            ? []
                                            : playback.onComplete,
                                    })
                                }
                            />
                            Loop this sequence
                        </label>
                        <label className="block">
                            Page volume
                            <input
                                className="w-full"
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={playback.volume ?? 1}
                                onChange={(e) =>
                                    savePlayback({
                                        volume: Number(e.target.value),
                                    })
                                }
                            />
                        </label>
                        <label className="flex gap-2">
                            <input
                                type="checkbox"
                                checked={playback.hideControls === true}
                                onChange={(e) =>
                                    savePlayback({
                                        hideControls: e.target.checked,
                                    })
                                }
                            />
                            Hide volume buttons on this page
                        </label>
                        {!playback.loop && (
                            <label className="block">
                                When audio finishes
                                <select
                                    className={field}
                                    value={completion?.action ?? ''}
                                    onChange={(e) =>
                                        savePlayback({
                                            onComplete: e.target.value
                                                ? [
                                                      {
                                                          action: e.target
                                                              .value,
                                                          data: {},
                                                      },
                                                  ]
                                                : [],
                                        })
                                    }
                                >
                                    <option value="">Stay on page</option>
                                    <option value="navigate">Go to page</option>
                                    <option value="capture">
                                        Start capture
                                    </option>
                                </select>
                                {completion?.action === 'navigate' && (
                                    <select
                                        className={field}
                                        value={
                                            completion.data.destination ?? ''
                                        }
                                        onChange={(e) =>
                                            savePlayback({
                                                onComplete: [
                                                    {
                                                        action: 'navigate',
                                                        data: {
                                                            destination:
                                                                e.target.value,
                                                        },
                                                    },
                                                ],
                                            })
                                        }
                                    >
                                        <option value="">Choose page…</option>
                                        {Object.keys(screens).map((id) => (
                                            <option key={id} value={id}>
                                                {id}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </label>
                        )}
                        <button
                            type="button"
                            className="text-red-700"
                            onClick={() =>
                                dispatch(
                                    updateScreen({
                                        id: screen.id,
                                        screen: { ...screen, audio: undefined },
                                    }),
                                )
                            }
                        >
                            Remove page audio
                        </button>
                    </>
                )}
                {experience?.telephone && (
                    <label className="block">
                        Telephone page behavior
                        <select
                            className={field}
                            value={screen.telephoneRole ?? 'call'}
                            onChange={(e) =>
                                dispatch(
                                    updateScreen({
                                        id: screen.id,
                                        screen: {
                                            ...screen,
                                            telephoneRole: e.target.value,
                                        },
                                    }),
                                )
                            }
                        >
                            {[
                                'idle',
                                'number',
                                'call',
                                'video',
                                'waitForHangup',
                                'photo',
                                'finish',
                            ].map((role) => (
                                <option key={role} value={role}>
                                    {role}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
        </details>
    );
}
